// Timeline for the first-load page loader. PageLoader.jsx (overlay + image box)
// and HeroHome.jsx (hero media scale-up, split-text delay, scroll lock) both
// derive their animations from these values — they never communicate at
// runtime, so everything here must stay deterministic.
//
// The loader is homepage-only: HeroHome is the sole block that mounts
// PageLoader, and it only does so on a first load. Nothing else on the site
// imports from this file. The loader window geometry lives in _pageLoader.scss
// (--loader-w / --loader-h) so it tracks the viewport.

import { CUSTOM_EASE } from '../../utils/animations';

export const LOADER_EASE = CUSTOM_EASE;

export const OPEN_DELAY = 0.5; // pause before the sliver starts opening
export const OPEN_DURATION = 0.5; // sliver → window
export const BEAT_START = OPEN_DELAY + 0.8; // first image cut
export const BEAT_INTERVAL = 0.6; // time each image is shown
export const BEAT_COUNT = 4; // = image count; last beat reveals the page
// How long before the last image disappears the overlay's hole mask engages
// — late enough that the image box (which fully covers the hole) has long
// since finished its opening animation, so nothing leaks through early.
export const HOLE_REVEAL_LEAD = 0.15;
// Absolute time of the final image cut, derived from the beat timeline above
// so changing BEAT_INTERVAL/BEAT_COUNT reflows everything after it.
export const LAST_BEAT_TIME = BEAT_START + (BEAT_COUNT - 1) * BEAT_INTERVAL;
// How long the last image holds on screen after its cut before the window
// starts expanding to full page.
export const POST_BEAT_HOLD = 0.45;
export const EXPAND_START = LAST_BEAT_TIME + POST_BEAT_HOLD; // window → full page
export const EXPAND_DURATION = 2;
export const LOADER_TOTAL = EXPAND_START + EXPAND_DURATION; // 5.55s
// How far into the expand the hero split-text starts its mid-reveal.
export const HERO_DELAY_OFFSET = 0.25;
export const HERO_DELAY = EXPAND_START + HERO_DELAY_OFFSET; // page-peek beat
// When the nav is released from its hold (see the .is-loading rules in
// NavigationDesktop/Mobile.scss) — as the page-peek window starts opening,
// so it fades in over the expand rather than popping in at the end.
export const NAV_RELEASE = EXPAND_START + 1.5;

// Fixed colour sequence for the loader overlay — one per beat, so
// LOADER_COLORS.length must stay BEAT_COUNT + 1. Deliberately hard-coded
// rather than read from --color-dynamic-* : those vars are white until
// dynamicColors.js applies the per-page order on the client, which would
// leave the first frames of the loader blank.
export const LOADER_COLORS = [
  'golden-meadow', // yellow
  'soft-moss', // green
  'spring-dawn', // orange
  'forest-thistle', // purple
  'morning-sky', // blue
];

// WebP at 810x608 — exactly 2x the loader window's max CSS size
// (--loader-w is min(402px, 72vw) in _pageLoader.scss), so it covers a 2x
// screen with nothing to spare. The JPG originals were 287KB for the set;
// these are 120KB.
export const LOADER_IMAGES = [
  '/images/loader-1.webp',
  '/images/loader-2.webp',
  '/images/loader-3.webp',
  '/images/loader-4.webp',
];

// How long the overlay/frame panels take to cross-fade between two beats'
// colours. Deliberately short — the cut should still read as a cut, just
// without the hard flash of an instant swap.
export const COLOR_FADE_DURATION = 0.1;

// The first image scales back to 1 from this value as the clip path opens the
// window — it shares OPEN_DELAY/OPEN_DURATION with that reveal. The later
// images cut in flat; only the opening frame gets the settle.
export const IMAGE_SCALE_FROM = 1.2;
